import i18nFactory from '@yandex-int/i18n';
import { cloneDeep, merge, values } from 'lodash';

import { TreeHelpers } from '../../service/TreeHelpers/TreeHelpers';
import { Experiment } from '../../typings/experimentsEditor';

import * as keyset from './ExperimentsEditorHelpers.i18n';

const i18n = i18nFactory(keyset);

export const defaultExperimentsValue = {
    experimentsTree: [],
    isSegmentTargetingEnabled: false,
    selectedMap: {},
    areCorrectionsEnabled: false,
    correctionsMap: {},
    archivedExperimentsMap: {},
    abSegmentRetargetingConditionId: undefined,
    abSegmentStatisticRetargetingConditionId: undefined,
    savedSelectedSegmentsIds: []
};

/**
 * Возвращает параметры дерева при инициализации
 * @param {Experiment[]} experimentsTree Список экспериметов
 * @param {Record<number, boolean>} selectedMap Выбранные элементы в дереве
 * @returns Объекты фильтрации и доступности экспериментов/сегментов
 */
export const getInitialExperimentMaps = (
    experimentsTree: Experiment[],
    selectedMap: Record<number, boolean>,
    activeExperimentsLimit: number
): {
    filteredMap: Record<number, boolean>;
    disabledMap: Record<number, boolean>;
} => {
    const allNodesEnabledMap = TreeHelpers.updateMapForCurrentIds(
        TreeHelpers.getChildNodesIds(experimentsTree),
        {},
        true
    );

    const disabledMap = experimentsTree.reduce((acc: Record<number, boolean>, experiment) => {
        const newDisabledMap = getSegmentsDisabledMap(experiment.inner, selectedMap);
        return merge(acc, newDisabledMap);
    }, {});

    return {
        filteredMap: allNodesEnabledMap,
        disabledMap: {
            ...getDisabledExperimentsMapIfLimit(experimentsTree, disabledMap, selectedMap, activeExperimentsLimit),
            ...disabledMap
        }
    };
};

/**
 * Делает все соседние сегменты доступными
 * @param {Experiment[]} experimentsTree Список экспериметов
 * @param {Record<number, boolean>} disabledMap Недоступные элементы в дереве
 * @param {Experiment} node Выбранный сегмент
 * @returns Объект доступности сегментов
 */
export const enableSiblingSegments = (
    experimentsTree: Experiment[],
    disabledMap: Record<number, boolean>,
    node: Experiment
): Record<number, boolean> => {
    const parent = TreeHelpers.getParents(experimentsTree, node);

    const siblingsIds = TreeHelpers.getChildNodesIds(parent[0].inner as Experiment[]);

    const newDisabledMap = cloneDeep(disabledMap);
    siblingsIds.forEach(id => {
        newDisabledMap[id] = false;
    });

    return newDisabledMap;
};

/**
 * Дизейблит сегмент, соседний с анчекнутым в дереве, если он - единственный выбранный в списке сегментов
 * @param {Experiment[]} experimentsTree Дерево экспериментов
 * @param {Experiment[]} deselectedSegment Анчекнутый сегмент
 * @param {Record<number, boolean>} selectedMap Выбранные элементы в дереве
 * @param {Record<number, boolean>} disabledMap Недоступные элементы в дереве
 * @returns {Record<number, boolean>} Объект с недоступными сегментами всего дерева
 */
export const getDisabledSegmentsMapByExperiment = (
    experimentsTree: Experiment[],
    deselectedSegment: Experiment,
    selectedMap: Record<number, boolean>,
    disabledMap: Record<number, boolean>
): Record<number, boolean> => {
    const siblingSegments = TreeHelpers.getParents(experimentsTree, deselectedSegment)[0].inner;

    const disabledSegments = getSegmentsDisabledMap(siblingSegments, selectedMap, deselectedSegment.id);

    return merge(disabledMap, disabledSegments);
};

/**
 * Дизейблит сегмент в списке сегментов, если он - единственный выбранный в списке сегментов
 * @param {Experiment[] | null} segments Список сегментов
 * @param {Record<number, boolean>} selectedMap Выбранные элементы в дереве
 * @param {string?} deselectedId id сегмента, который был анчекнут
 * @returns {Record<number, boolean>} Объект с неактивными сегментами
 */
export const getSegmentsDisabledMap = (
    segments: Experiment[] | null,
    selectedMap: Record<number, boolean>,
    deselectedId?: string
): Record<number, boolean> => {
    if (segments === null) {
        return {};
    }

    const childrenIds = TreeHelpers.getChildNodesIds(segments);

    const childrenSelectedMap = childrenIds.reduce((acc: Record<number, boolean>, id) => {
        if ((!deselectedId || deselectedId !== id) && selectedMap[id]) {
            acc[id] = selectedMap[id];
        }

        return acc;
    }, {});

    const selectedSegmentsKeys = Object.keys(childrenSelectedMap);
    const isManySelectedSegments = selectedSegmentsKeys.length !== 1;
    if (isManySelectedSegments) {
        return {};
    }

    return {
        [selectedSegmentsKeys[0]]: true
    };
};

/**
 * Делает все эксперименты доступными
 * @param {Experiment[]} experimentsTree Список экспериметов
 * @param {Record<number, boolean>} disabledMap Недоступные элементы в дереве
 * @returns Объект доступности сегментов
 */
export const enableExperiments = (
    experimentsTree: Experiment[],
    disabledMap: Record<number, boolean>,
    archivedExperimentsMap: Record<number, boolean>
): Record<number, boolean> => {
    const experimentsDisabledMap = experimentsTree.reduce((acc, exp) => {
        return archivedExperimentsMap[exp.id] ? acc : { ...acc, [exp.id]: false };
    }, {});

    return {
        ...disabledMap,
        ...experimentsDisabledMap
    };
};

/**
 * Дизейблит остальные эксперименты, если достигнут лимит активных экспериментов
 * @param {Experiment[]} experimentsTree Дерево экспериментов
 * @param {Record<number, boolean>} disabledMap Недоступные элементы в дереве
 * @param {Record<number, boolean>} selectedMap Выбранные элементы в дереве
 * @returns {Record<number, boolean>} Объект с недоступными сегментами всего дерева
 */
export const getDisabledExperimentsMapIfLimit = (
    experimentsTree: Experiment[],
    disabledMap: Record<number, boolean>,
    selectedMap: Record<number, boolean>,
    activeExperimentsLimit: number
): Record<number, boolean> => {
    const selectedExperimentsMap = experimentsTree.reduce((acc, exp) => {
        acc[exp.id] = selectedMap[exp.id];
        return acc;
    }, {});

    const selectedExperimentsCount = values(selectedExperimentsMap).filter(el => el).length;

    if (selectedExperimentsCount >= activeExperimentsLimit) {
        const newDeselectedExperiments = experimentsTree.reduce((acc, exp) => {
            if (!selectedMap[exp.id]) {
                acc[exp.id] = true;
            }
            return acc;
        }, {});

        return {
            ...disabledMap,
            ...newDeselectedExperiments
        };
    }

    return disabledMap;
};

export const getArchivedExperimentName = (experimentId: string): string => {
    return i18n('archived-experiment-name', { experimentId });
};

export const getArchivedExperimentsCount = (archivedExperimentsMap: Record<string, boolean>): number => {
    return values(archivedExperimentsMap).filter(isArchived => isArchived).length;
};

export const isSegmentRateCorrectionEnabled = (
    isSegmentTargetingEnabled: boolean,
    isCurrentSegmentSelected: boolean,
    isParentExperimentSelected: boolean
): boolean => {
    return (!isSegmentTargetingEnabled || isCurrentSegmentSelected) && isParentExperimentSelected;
};

export const getExperimentsFilteredByArchiveParameter = (
    experiments: Experiment[],
    archivedExperimentsMap: Record<number, boolean>,
    needArchived: boolean
): Experiment[] => {
    return experiments.filter(experiment => {
        const isArchived = archivedExperimentsMap[experiment.id];
        return needArchived ? isArchived : !isArchived;
    });
};
